/* eslint-disable react-refresh/only-export-components --
   `levelForScore` is a pure helper co-located with the RiskMatrix component and
   reused by the risk pages; the react-refresh rule is dev-only HMR guidance. */
import type { RiskMatrix as RiskMatrixConfig, RiskLevel } from '../../types/whs';
import { RISK_LEVEL_CELL, RISK_LEVEL_LABEL } from './whsConstants';

export function levelForScore(score: number, matrix: RiskMatrixConfig): RiskLevel {
  const bands = matrix.bands && matrix.bands.length ? matrix.bands : [
    { level: 'low' as RiskLevel, max: 3 },
    { level: 'moderate' as RiskLevel, max: 8 },
    { level: 'high' as RiskLevel, max: 14 },
    { level: 'extreme' as RiskLevel, max: 25 },
  ];
  for (const b of bands) if (score <= b.max) return b.level;
  return bands[bands.length - 1].level;
}

interface RiskMatrixProps {
  matrix: RiskMatrixConfig;
  selectedLikelihood?: number | null;
  selectedConsequence?: number | null;
  onSelect?: (likelihood: number, consequence: number) => void;
  compact?: boolean;
}

/**
 * 5×5 likelihood (rows) × consequence (columns) matrix. Cells are colour-coded
 * by the derived risk level. When `onSelect` is supplied the grid is an input
 * (click a cell to pick likelihood/consequence); otherwise it's a legend.
 */
export default function RiskMatrix({
  matrix, selectedLikelihood, selectedConsequence, onSelect, compact,
}: RiskMatrixProps) {
  const likelihoods = matrix.likelihood_labels || [];
  const consequences = matrix.consequence_labels || [];
  const cell = compact ? 'h-9 text-[11px]' : 'h-12 text-xs';

  return (
    <div className="overflow-x-auto">
      <table className="border-collapse">
        <thead>
          <tr>
            <th className="p-1" />
            <th className="p-1 text-[10px] font-semibold text-neutral-500 uppercase" colSpan={consequences.length}>
              Consequence →
            </th>
          </tr>
          <tr>
            <th className="p-1 text-[10px] font-semibold text-neutral-500 text-right pr-2">Likelihood ↓</th>
            {consequences.map((c, ci) => (
              <th key={ci} className="p-1 text-[10px] font-medium text-neutral-600 min-w-[64px]">
                {ci + 1}. {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {likelihoods.map((l, li) => {
            const likelihood = li + 1;
            return (
              <tr key={li}>
                <th className="p-1 text-[10px] font-medium text-neutral-600 text-right pr-2 whitespace-nowrap">
                  {likelihood}. {l}
                </th>
                {consequences.map((_, ci) => {
                  const consequence = ci + 1;
                  const score = likelihood * consequence;
                  const level = levelForScore(score, matrix);
                  const isSelected = selectedLikelihood === likelihood && selectedConsequence === consequence;
                  return (
                    <td key={ci} className="p-0.5">
                      <button
                        type="button"
                        disabled={!onSelect}
                        onClick={() => onSelect?.(likelihood, consequence)}
                        title={`${RISK_LEVEL_LABEL[level]} (score ${score})`}
                        className={[
                          'w-full rounded font-semibold flex items-center justify-center transition',
                          cell,
                          RISK_LEVEL_CELL[level],
                          onSelect ? 'cursor-pointer hover:opacity-90' : 'cursor-default',
                          isSelected ? 'ring-2 ring-offset-1 ring-neutral-900' : '',
                        ].join(' ')}
                      >
                        {score}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
